import { runtimeExecutionAuthorityOpenMeldServiceRuntimeIdSchema } from "./execution-identity.js";
import { runtimeCoordinatorSafeEventPayloadJsonSchema } from "./runtime-coordinator-execution-token.js";
import * as z$1 from "zod/v4";
//#region src/schemas/daemon/runtime-coordinator-execution.ts
const RUNTIME_COORDINATOR_EXECUTION_MODEL_VERSION = "openmeld.runtime_coordinator.execution.v1";
const RUNTIME_COORDINATOR_CREATION_DIAGNOSTIC_MODEL_VERSION = "openmeld.runtime_coordinator.creation_diagnostic.v1";
const boundedIdentifierSchema = z$1.string().trim().min(1).max(512);
const boundedReasonCodeSchema = z$1.string().trim().min(1).max(160);
const nullableSourceClientMessageIdSchema = z$1.string().trim().min(1).max(200).nullable();
const nullableIdentifierSchema = boundedIdentifierSchema.nullable();
const nullablePositiveIntegerSchema = z$1.number().int().positive().nullable();
const runtimeCoordinatorExecutionStatusSchema = z$1.enum([
	"created",
	"delivery_pending",
	"delivery_accepted",
	"delivery_rejected",
	"cancellation_requested",
	"accepted_local",
	"running",
	"completed_local",
	"result_received",
	"result_accepted",
	"reply_published",
	"cancelled",
	"failed_retryable",
	"failed_terminal",
	"state_unknown"
]);
const runtimeCoordinatorExecutionEventTypeSchema = z$1.enum([
	"execution_created",
	"delivery_accepted",
	"delivery_rejected",
	"cancellation_requested",
	"cancellation_delivery_accepted",
	"cancellation_delivery_rejected",
	"cancellation_control_accepted",
	"cancellation_control_rejected",
	"lifecycle_accepted_local",
	"lifecycle_running",
	"lifecycle_completed_local",
	"result_received",
	"result_accepted",
	"result_duplicate",
	"result_stale_lease",
	"result_stale_attempt",
	"result_connection_generation_mismatch",
	"result_identity_echo_mismatch",
	"result_late_diagnostic",
	"execution_token_issued",
	"execution_token_validation_failed",
	"execution_token_superseded",
	"execution_token_expired",
	"execution_token_revoked",
	"execution_token_terminal_late",
	"token_history_retained",
	"token_history_pruned",
	"runtime_coordinator_execution_id_missing",
	"runtime_coordinator_execution_id_mismatch",
	"runtime_evidence_observed",
	"runtime_evidence_identity_echo_mismatch",
	"owner_conflict_current_token",
	"owner_conflict_stale_token",
	"owner_conflict_missing_token",
	"runtime_coordinator_decision_recorded",
	"runtime_coordinator_redelivery_commanded",
	"runtime_coordinator_owner_conflict_terminal",
	"runtime_coordinator_reclaim_blocked",
	"runtime_coordinator_retry_blocked",
	"runtime_coordinator_timeout_terminal",
	"reply_published",
	"reply_publication_failed",
	"coordinator_pending_state_missing",
	"runtime_connection_lost"
]);
const runtimeCoordinatorTerminalOutcomeSchema = z$1.strictObject({
	kind: z$1.enum([
		"reply_published",
		"failed_retryable",
		"failed_terminal",
		"cancelled",
		"timed_out"
	]),
	reasonCode: boundedReasonCodeSchema.nullable(),
	observedAtMs: z$1.number().int().nonnegative()
});
const runtimeCoordinatorCancellationStatusSchema = z$1.enum([
	"not_requested",
	"requested",
	"delivery_pending",
	"delivery_rejected",
	"local_control_accepted",
	"local_control_rejected",
	"unsupported",
	"cancelled"
]);
const runtimeCoordinatorCancellationStateSchema = z$1.strictObject({
	status: runtimeCoordinatorCancellationStatusSchema,
	reasonCode: boundedReasonCodeSchema.nullable(),
	observedAtMs: z$1.number().int().nonnegative().nullable()
});
const runtimeCoordinatorLegacyPointersSchema = z$1.strictObject({
	attemptId: nullableIdentifierSchema,
	claimLeaseId: nullableIdentifierSchema,
	daemonSessionId: nullableIdentifierSchema,
	connectionGeneration: nullablePositiveIntegerSchema,
	leaseEpoch: nullablePositiveIntegerSchema
});
const runtimeCoordinatorExecutionRecordSchema = z$1.strictObject({
	runtimeCoordinatorExecutionId: boundedIdentifierSchema,
	modelVersion: z$1.literal(RUNTIME_COORDINATOR_EXECUTION_MODEL_VERSION),
	spaceId: boundedIdentifierSchema,
	dispatchId: boundedIdentifierSchema,
	targetProfileId: boundedIdentifierSchema,
	sourceClientMessageId: nullableSourceClientMessageIdSchema,
	openMeldServiceRuntimeId: runtimeExecutionAuthorityOpenMeldServiceRuntimeIdSchema,
	bindingId: boundedIdentifierSchema,
	agentControllerRef: boundedIdentifierSchema,
	status: runtimeCoordinatorExecutionStatusSchema,
	terminalOutcome: runtimeCoordinatorTerminalOutcomeSchema.nullable(),
	createdAtMs: z$1.number().int().nonnegative(),
	updatedAtMs: z$1.number().int().nonnegative(),
	legacyPointers: runtimeCoordinatorLegacyPointersSchema
}).superRefine((record, context) => {
	if (record.status === "cancelled" && record.terminalOutcome?.kind !== "cancelled") context.addIssue({
		code: "custom",
		message: "cancelled RuntimeCoordinator executions require a cancelled terminal outcome",
		path: ["terminalOutcome"]
	});
	if (record.status === "cancellation_requested" && record.terminalOutcome !== null) context.addIssue({
		code: "custom",
		message: "cancellation_requested RuntimeCoordinator executions must not have a terminal outcome",
		path: ["terminalOutcome"]
	});
});
const runtimeCoordinatorExecutionEventSchema = z$1.strictObject({
	eventId: boundedIdentifierSchema,
	runtimeCoordinatorExecutionId: boundedIdentifierSchema,
	eventType: runtimeCoordinatorExecutionEventTypeSchema,
	statusAfter: runtimeCoordinatorExecutionStatusSchema.nullable(),
	reasonCode: boundedReasonCodeSchema.nullable(),
	observedAtMs: z$1.number().int().nonnegative(),
	idempotencyKey: boundedIdentifierSchema,
	payloadJson: runtimeCoordinatorSafeEventPayloadJsonSchema
});
const runtimeCoordinatorExecutionCreationStatusSchema = z$1.enum(["not_created"]);
const runtimeCoordinatorExecutionCreationDiagnosticSchema = z$1.strictObject({
	modelVersion: z$1.literal(RUNTIME_COORDINATOR_CREATION_DIAGNOSTIC_MODEL_VERSION),
	spaceId: boundedIdentifierSchema,
	dispatchId: boundedIdentifierSchema,
	targetProfileId: boundedIdentifierSchema,
	sourceClientMessageId: nullableSourceClientMessageIdSchema,
	creationStatus: runtimeCoordinatorExecutionCreationStatusSchema,
	reasonCode: boundedReasonCodeSchema,
	observedAtMs: z$1.number().int().nonnegative(),
	legacyAttemptId: nullableIdentifierSchema
});
const buildRuntimeCoordinatorExecutionId = (input) => {
	const spaceId = normalizeRequiredRuntimeCoordinatorIdPart("spaceId", input.spaceId);
	const dispatchId = normalizeRequiredRuntimeCoordinatorIdPart("dispatchId", input.dispatchId);
	const targetProfileId = normalizeRequiredRuntimeCoordinatorIdPart("targetProfileId", input.targetProfileId);
	return [
		"rce_v1",
		encodeLengthPrefixedIdPart(spaceId),
		encodeLengthPrefixedIdPart(dispatchId),
		encodeLengthPrefixedIdPart(targetProfileId)
	].join("|");
};
const buildRuntimeCoordinatorExecutionEventId = (input) => {
	const runtimeCoordinatorExecutionId = normalizeRequiredRuntimeCoordinatorIdPart("runtimeCoordinatorExecutionId", input.runtimeCoordinatorExecutionId);
	const idempotencyKey = normalizeRequiredRuntimeCoordinatorIdPart("idempotencyKey", input.idempotencyKey);
	return [
		"rce_event_v1",
		encodeLengthPrefixedIdPart(runtimeCoordinatorExecutionId),
		encodeLengthPrefixedIdPart(input.eventType),
		encodeLengthPrefixedIdPart(idempotencyKey)
	].join("|");
};
const encodeLengthPrefixedIdPart = (value) => {
	return `${value.length}:${value}`;
};
const normalizeRequiredRuntimeCoordinatorIdPart = (fieldName, value) => {
	const normalized = value.trim();
	if (!normalized) throw new Error(`runtime_coordinator.id_invalid: ${fieldName} is required`);
	return normalized;
};
//#endregion
export { RUNTIME_COORDINATOR_CREATION_DIAGNOSTIC_MODEL_VERSION, RUNTIME_COORDINATOR_EXECUTION_MODEL_VERSION, buildRuntimeCoordinatorExecutionEventId, buildRuntimeCoordinatorExecutionId, runtimeCoordinatorCancellationStateSchema, runtimeCoordinatorCancellationStatusSchema, runtimeCoordinatorExecutionCreationDiagnosticSchema, runtimeCoordinatorExecutionCreationStatusSchema, runtimeCoordinatorExecutionEventSchema, runtimeCoordinatorExecutionEventTypeSchema, runtimeCoordinatorExecutionRecordSchema, runtimeCoordinatorExecutionStatusSchema, runtimeCoordinatorLegacyPointersSchema, runtimeCoordinatorTerminalOutcomeSchema };

//# sourceMappingURL=runtime-coordinator-execution.js.map